import React from 'react';
import { Links, Meta, Outlet, Scripts, ScrollRestoration, useLocation } from 'react-router';

// index.css owns theme imports and their order, as well as Tailwind.
import '../src/index.css';
import { TanStackQueryProvider } from '../src/lib/react-query';
import { AuthProvider } from '../src/lib/auth';
import { ErrorTelemetryProvider } from '../src/context/ErrorTelemetryProvider';
import { ErrorToastProvider } from '../src/context/ErrorToastContext';
import { GlobalErrorToast } from '../src/components/feedback/GlobalErrorToast';
import { AppErrorBoundary } from '../src/components/errors/AppErrorBoundary';
import RouteExperience from './layouts/RouteExperience';
import { ThemeProvider } from '../src/context/ThemeContext/ThemeContext';
import AnalyticsRouteListener from '../src/components/AnalyticsRouteListener';
import { DocumentHeadProvider } from '../src/components/DocumentHead';
import ThirdPartyBootstrap from '../src/components/ThirdPartyBootstrap/ThirdPartyBootstrap';
import { DocumentHydrateFallback } from '../src/components/RouteHydrateFallbacks/RootHydrateFallback';
import { GTM_ANALYTICS_CONTEXT_SCRIPT, GTM_HEAD_SCRIPT, GTM_NOSCRIPT_SRC } from '../src/lib/gtm';
import { PRODUCTION_CONTENT_SECURITY_POLICY } from './lib/contentSecurityPolicy';
import { RoutePlayerPersistenceProvider } from '../src/lib/auth/PlayerPersistenceContext';
import { isArticlesPathname, isNewsletterMarketingPathname } from '../src/routes';
import { GameAdsProvider } from '../src/features/gameAds';

export function Layout({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const defaults = {
    theme: 'light',
    material: 'glass',
    view: 'default',
  };

  const clientHtml = typeof document === 'undefined' ? null : document.documentElement;
  const theme = clientHtml?.getAttribute('data-theme') ?? defaults.theme;
  const material = clientHtml?.getAttribute('data-material') ?? defaults.material;
  const view = clientHtml?.getAttribute('data-view') ?? defaults.view;
  const stripeCheckoutReady =
    Boolean(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) &&
    import.meta.env.VITE_STRIPE_CHECKOUT_ENABLED === 'true';

  return (
    <html
      lang="en"
      data-theme={theme}
      data-material={material}
      data-view={view}
      suppressHydrationWarning
    >
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        {/* Google Tag Manager — required on every page. Do not remove, defer, or gate. */}
        <script dangerouslySetInnerHTML={{ __html: GTM_ANALYTICS_CONTEXT_SCRIPT }} />
        <script dangerouslySetInnerHTML={{ __html: GTM_HEAD_SCRIPT }} />

        {/* Articles keep the app's title and body faces under the marketing navigation. */}
        {(!isNewsletterMarketingPathname(pathname) || isArticlesPathname(pathname)) && (
          <>
            <link
              rel="preload"
              href="/fonts/LibreFranklin-VariableFont_wght.woff2"
              as="font"
              type="font/woff2"
              crossOrigin="anonymous"
            />

            <link
              rel="preload"
              href="/fonts/Lato-400.woff2"
              as="font"
              type="font/woff2"
              crossOrigin="anonymous"
            />
          </>
        )}
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link rel="icon" type="image/png" href="/favicon.png" />
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
        <link rel="manifest" href="/manifest.webmanifest" />
        <link rel="preconnect" href="https://gtlwpwczbqfepuxphjyr.supabase.co" />
        <link rel="dns-prefetch" href="//gtlwpwczbqfepuxphjyr.supabase.co" />
        <link
          rel="preconnect"
          href="https://fundingchoicesmessages.google.com"
          crossOrigin="anonymous"
        />
        <link rel="dns-prefetch" href="//fundingchoicesmessages.google.com" />
        {import.meta.env.PROD ? (
          <meta httpEquiv="Content-Security-Policy" content={PRODUCTION_CONTENT_SECURITY_POLICY} />
        ) : null}
        <meta name="atrivial-build-id" content={import.meta.env.VITE_APP_BUILD_ID ?? 'unknown'} />
        <meta name="atrivial-checkout-ready" content={stripeCheckoutReady ? 'true' : 'false'} />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function () {
                // Default materials for each theme (mirrors ThemeContext DEFAULT_MATERIALS)
                var DEFAULT_MATERIALS = {
                  light: 'glass',
                  dark: 'glass',
                  device: 'glass',
                  assembly: 'flat',
                  buzzfeed: 'glass',
                  history: 'flat',
                  indyweek: 'flat'
                };

                try {
                  var html = document.documentElement;
                  var params = new URLSearchParams(window.location.search);
                  var isIframe = params.get('view') === 'iframe';
                  html.setAttribute('data-view', isIframe ? 'iframe' : 'default');
                  var parseCanonicalTheme = function (value) {
                    if (!value) return null;
                    var normalized = String(value).trim().toLowerCase();
                    return (
                      normalized === 'device' ||
                      normalized === 'dark' ||
                      normalized === 'light' ||
                      normalized === 'history' ||
                      normalized === 'buzzfeed' ||
                      normalized === 'indyweek' ||
                      normalized === 'assembly'
                    )
                      ? normalized
                      : null;
                  };
                  var resolveThemePreference = function (preference) {
                    return preference === 'device'
                      ? ((window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) ? 'dark' : 'light')
                      : preference;
                  };

                  var resolved;
                  var material;
                  if (isIframe) {
                    // Match ThemeContext getInitialTheme(): embedded delivery starts neutral
                    // until its authoritative runtime presentation resolves.
                    resolved = 'light';
                    material = DEFAULT_MATERIALS[resolved] || 'glass';
                  } else {
                    // Keep first paint aligned with ThemeContext getInitialTheme().
                    var storedTheme = null;
                    try {
                      storedTheme = parseCanonicalTheme(localStorage.getItem('theme'));
                    } catch {}

                    var preference = storedTheme || 'device';
                    resolved = resolveThemePreference(preference);

                    var storedMaterial = null;
                    try {
                      var candidateMaterial = localStorage.getItem('material');
                      if (
                        candidateMaterial === 'glass' ||
                        candidateMaterial === 'flat' ||
                        candidateMaterial === 'neumorphic'
                      ) {
                        storedMaterial = candidateMaterial;
                      }
                    } catch {}
                    material = storedMaterial || DEFAULT_MATERIALS[resolved] || 'glass';
                  }

                  html.setAttribute('data-theme', resolved);
                  html.setAttribute('data-material', material);
                } catch (e) {
                  var html = document.documentElement;
                  html.setAttribute('data-theme', 'light');
                  html.setAttribute('data-material', 'glass');
                  html.setAttribute('data-view', 'default');
                }
              })();
            `,
          }}
        />
        <style
          dangerouslySetInnerHTML={{
            __html: `
              html {
                color-scheme: light;
              }
              html[data-theme="dark"] {
                color-scheme: dark;
              }
              html[data-theme="light"] {
                background-color: var(--embed-outer-canvas, var(--bg-primary, oklch(0.99 0.003 240)));
              }
              html[data-theme="history"] {
                background-color: var(--embed-outer-canvas, var(--bg-primary, oklch(1 0 0)));
              }
              html[data-theme="dark"] {
                background-color: var(--embed-outer-canvas, var(--bg-primary, oklch(0.22 0.01 240)));
              }
              body {
                background-color: var(--embed-outer-canvas, var(--bg-primary, inherit));
              }
            `,
          }}
        />
        <Meta />
        <Links />
      </head>
      <body suppressHydrationWarning>
        {/* Google Tag Manager (noscript) — required on every page. Do not remove. */}
        <noscript>
          <iframe
            src={GTM_NOSCRIPT_SRC}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
            title="Google Tag Manager"
          />
        </noscript>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function PlayerPersistenceRoot() {
  const { pathname } = useLocation();
  return (
    <AuthProvider deferSessionDiscovery={isNewsletterMarketingPathname(pathname)}>
      <ErrorTelemetryProvider>
        <ErrorToastProvider>
          <AppErrorBoundary>
            <ThemeProvider>
              <AnalyticsRouteListener />
              <ThirdPartyBootstrap />
              <DocumentHeadProvider>
                <RouteExperience>
                  <Outlet />
                </RouteExperience>
              </DocumentHeadProvider>
            </ThemeProvider>
            <GlobalErrorToast />
          </AppErrorBoundary>
        </ErrorToastProvider>
      </ErrorTelemetryProvider>
    </AuthProvider>
  );
}

export default function Root() {
  return (
    <TanStackQueryProvider>
      <RoutePlayerPersistenceProvider>
        <GameAdsProvider>
          <PlayerPersistenceRoot />
        </GameAdsProvider>
      </RoutePlayerPersistenceProvider>
    </TanStackQueryProvider>
  );
}

// The SPA document is shared by deep links, so its first hydration must be route-independent.
// RouteExperience and route-level fallbacks retain their route-specific loading UI.
export function HydrateFallback() {
  return <DocumentHydrateFallback />;
}
